import { createNewSpreadsheet, getSpreadsheet } from "./spreadsheets.service";
import { isStandaloneMode } from "../utils/standalone-mode";

// Configs for custom XML storage
const XML_NAMESPACE = "http://workflowintelligence.com/metadata/1.0";
const XML_PART_ID_SETTING = "WFI_MetadataXmlPartId";

// Workbook settings keys
const TAB_COLOURING_ENABLED_SETTING = "WFI_TabColouringEnabled";

/**
 * Creates the XML string for storing metadata
 */
function createMetadataXml(spreadsheetId: string): string {
    return `<WFIMetadata xmlns='${XML_NAMESPACE}'><SpreadsheetId>${spreadsheetId}</SpreadsheetId></WFIMetadata>`;
}

/**
 * Parses the spreadsheet ID from the metadata XML
 */
function parseSpreadsheetIdFromXml(xml: string): string | null {
    const match = xml.match(/<SpreadsheetId>([^<]*)<\/SpreadsheetId>/);
    return match ? match[1] : null;
}

/**
 * Read the spreadsheet ID from the workbook's custom XML part. Propagates
 * Excel errors — used by write paths that must never overwrite an existing
 * ID silently. If the read itself fails, the caller should surface the error
 * rather than assume "no metadata."
 */
async function readSpreadsheetIdFromWorkbook(): Promise<string | null> {
    return await Excel.run(async (context) => {
        // Get the stored XML part ID from workbook settings
        const settings = context.workbook.settings;
        const xmlPartIdSetting = settings.getItemOrNullObject(XML_PART_ID_SETTING);
        xmlPartIdSetting.load("value");
        await context.sync();

        if (xmlPartIdSetting.isNullObject) {
            return null;
        }

        // Get the custom XML part using the stored ID
        const customXmlPart = context.workbook.customXmlParts.getItemOrNullObject(xmlPartIdSetting.value);
        await context.sync();

        if (customXmlPart.isNullObject) {
            // XML part was deleted, clean up the stale setting
            xmlPartIdSetting.delete();
            await context.sync();
            return null;
        }

        // Get the XML content and parse the spreadsheet ID
        const xmlBlob = customXmlPart.getXml();
        await context.sync();

        return parseSpreadsheetIdFromXml(xmlBlob.value);
    });
}

export async function getOpenSpreadsheetId(): Promise<string | null> {
    try {
        return await readSpreadsheetIdFromWorkbook();
    } catch (error) {
        console.error("Error getting spreadsheet ID:", error);
        return null;
    }
}

/**
 * Read the workbook's spreadsheet ID and verify it exists on the backend.
 * Returns null when the workbook has no metadata OR when the cached ID doesn't
 * resolve on the backend — the caller is responsible for handling the fresh /
 * unlinked case (e.g. showing Setup and prompting the user to initialise).
 *
 * Intentionally does NOT auto-create on miss. Auto-create used to live here
 * and defeated the Unlink flow — the addin would reload, find no metadata,
 * and immediately stamp a fresh ID back into the workbook. Creation is now
 * explicit via `createSpreadsheetForNewWorkbook` on Setup save.
 */
/**
 * Resolve the current workbook's spreadsheet ID for the current host.
 *
 * - **Excel mode**: read from the workbook's custom XML part.
 * - **Standalone mode**: read from `?spreadsheetId=` on the current URL.
 *
 * Either way, the returned ID is verified against the backend. Returns null if
 * no metadata is present OR the ID doesn't resolve on the backend. The caller
 * is responsible for handling the fresh / unlinked case (Excel → show Setup
 * so it can create one; standalone → show the missing-id error page).
 *
 * Callers don't need to know which host they're in — this is the single
 * "give me the spreadsheet ID" entry point.
 */
export async function getSpreadsheetMetadata(): Promise<string | null> {
    const spreadsheetId = isStandaloneMode()
        ? new URLSearchParams(window.location.search).get("spreadsheetId")
        : await getOpenSpreadsheetId();
    if (!spreadsheetId) return null;

    try {
        await getSpreadsheet(spreadsheetId);
        return spreadsheetId;
    } catch (error) {
        // Excel mode: stale ID (deleted from backend) — treat as no-metadata.
        //   Setup Save will create a fresh ID and overwrite the stale XML part.
        // Standalone mode: URL param points at an ID the backend doesn't know
        //   about. Returning null bubbles up to the missing-id error page.
        console.warn(`Spreadsheet ID ${spreadsheetId} not found on backend`, error);
        return null;
    }
}

/**
 * Create a brand-new spreadsheet row on the backend and stamp its ID into the
 * workbook's custom XML part. Called from Setup Save when the workbook has no
 * existing metadata.
 *
 * SAFETY: refuses to overwrite an existing XML-part ID. Callers might arrive
 * here believing the workbook is fresh because `getSpreadsheetMetadata`
 * returned null — but that null can also mean "backend transiently failed to
 * confirm a valid ID." Re-reading the raw XML (with error-propagation, not
 * error-swallowing) is the source of truth. If a real ID is already stamped
 * in, we return it unchanged and let downstream API calls fail visibly rather
 * than clobber it and orphan the workbook's discussions/status items.
 *
 * The only sanctioned way to clear or change the ID is the Unlink / Override
 * controls in Settings.
 */
export async function createSpreadsheetForNewWorkbook(): Promise<string> {
    const existingId = await readSpreadsheetIdFromWorkbook();
    if (existingId) return existingId;

    const workbookName = await getWorkbookName();
    const newSpreadsheet = await createNewSpreadsheet(workbookName);
    await saveSpreadsheetId(newSpreadsheet.documentId);
    return newSpreadsheet.documentId;
}

/**
 * Gets the workbook name from the Excel workbook properties
 */
async function getWorkbookName(): Promise<string> {
    return await Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.load("name");
        await context.sync();
        return workbook.name;
    });
}

/**
 * Saves the spreadsheet ID to a custom XML part AND flushes the workbook to
 * disk. The disk flush is load-bearing: without it, the XML part lives only
 * in Excel's in-memory model, and a user who closes the workbook without
 * hitting File→Save (or clicks "Don't Save" on the close prompt) loses the
 * metadata. On the next open we can't find the ID, create a fresh spreadsheet
 * row, and orphan whatever discussions / status items / documents were
 * created against the previous in-memory row.
 *
 * context.workbook.save() triggers Excel's normal save flow — silent on a
 * workbook already backed by a file, prompts only for a never-saved workbook
 * (which shouldn't happen in the addin's runtime context).
 */
async function saveSpreadsheetId(spreadsheetId: string): Promise<void> {
    await Excel.run(async (context) => {
        const settings = context.workbook.settings;

        // Check if we have an existing XML part to update
        const existingIdSetting = settings.getItemOrNullObject(XML_PART_ID_SETTING);
        existingIdSetting.load("value");
        await context.sync();

        if (!existingIdSetting.isNullObject) {
            // Delete the existing XML part
            const existingPart = context.workbook.customXmlParts.getItemOrNullObject(existingIdSetting.value);
            await context.sync();

            if (!existingPart.isNullObject) {
                existingPart.delete();
            }
        }

        // Create a new custom XML part with the metadata
        const xmlContent = createMetadataXml(spreadsheetId);
        const customXmlPart = context.workbook.customXmlParts.add(xmlContent);
        customXmlPart.load("id");
        await context.sync();

        // Store the XML part ID in workbook settings for future retrieval
        settings.add(XML_PART_ID_SETTING, customXmlPart.id);
        await context.sync();

        // Force the file to disk so the metadata survives a "Don't Save" close.
        // Without this, the user's next open finds no XML part and the addin
        // orphans the previous DB row by creating a fresh one.
        context.workbook.save();
        await context.sync();
    });
}

/**
 * Repoint the current workbook at a specific spreadsheet ID. Same underlying
 * flow as the internal saveSpreadsheetId — exists as a public export for the
 * Settings-page debug controls (override / recovery from template-distribution
 * accidents). The workbook is force-saved so the new ID persists to disk.
 */
export async function overrideSpreadsheetId(spreadsheetId: string): Promise<void> {
    await saveSpreadsheetId(spreadsheetId);
}

/**
 * Remove the WFI metadata from the workbook entirely — delete the custom XML
 * part and the tracking setting. The next call to getSpreadsheetMetadata
 * returns null, prompting Setup and (only on Save) a fresh spreadsheet row.
 *
 * Used to un-link a workbook that was accidentally distributed with a WFI ID
 * baked in (e.g. a template that everyone opened, all writing to the same
 * spreadsheet row). Force-saves so the deletion is persisted immediately —
 * without it, "Don't Save" on close would leave the metadata intact.
 */
export async function clearSpreadsheetMetadata(): Promise<void> {
    await Excel.run(async (context) => {
        const settings = context.workbook.settings;

        const existingIdSetting = settings.getItemOrNullObject(XML_PART_ID_SETTING);
        existingIdSetting.load("value");
        await context.sync();

        if (!existingIdSetting.isNullObject) {
            const existingPart = context.workbook.customXmlParts.getItemOrNullObject(existingIdSetting.value);
            await context.sync();

            if (!existingPart.isNullObject) {
                existingPart.delete();
            }
            existingIdSetting.delete();
            await context.sync();
        }

        // Persist to disk so a "Don't Save" close doesn't resurrect the metadata.
        context.workbook.save();
        await context.sync();
    });
}

/**
 * Get the tab colouring enabled setting
 * @returns true if tab colouring is enabled (default: true)
 */
export async function getTabColouringEnabled(): Promise<boolean> {
    try {
        return await Excel.run(async (context) => {
            const settings = context.workbook.settings;
            const setting = settings.getItemOrNullObject(TAB_COLOURING_ENABLED_SETTING);
            setting.load("value");
            await context.sync();

            if (setting.isNullObject) {
                // Default to enabled if not set
                return true;
            }

            return setting.value === "true";
        });
    } catch (error) {
        console.error("Error getting tab colouring setting:", error);
        return true; // Default to enabled on error
    }
}

/**
 * Set the tab colouring enabled setting
 * @param enabled - Whether tab colouring should be enabled
 */
export async function setTabColouringEnabled(enabled: boolean): Promise<void> {
    try {
        await Excel.run(async (context) => {
            const settings = context.workbook.settings;
            settings.add(TAB_COLOURING_ENABLED_SETTING, enabled.toString());
            await context.sync();
        });
    } catch (error) {
        console.error("Error setting tab colouring setting:", error);
        throw error;
    }
}